import React, { useContext, useEffect, useState } from "react";
import { Select } from "antd";
import DoughnutChart from "./DoughnutChart";
import { MyContext } from "../../../context/myContext";
import { Metric } from "../../../interfaces/IMetricChart";
import { BreakdownType } from "../PerformanceDashboard";



interface MetricDropDownProps {
  metrics: Metric[];
  updateFilter: (key: string, value: any) => void
  selectedMetric: string | undefined
  setSelectedMetric: React.Dispatch<React.SetStateAction<string | undefined>>
  averageValue: string
  setAverageValue: React.Dispatch<React.SetStateAction<string>>
  selectedBreakdownType:string
  setSelectedBreakdownType: React.Dispatch<React.SetStateAction<string>>
}

const MetricDropdown: React.FC<MetricDropDownProps> = ({ metrics, updateFilter, selectedMetric, setSelectedMetric, averageValue, setAverageValue, setSelectedBreakdownType, selectedBreakdownType }) => {
  const { loading, setLoading } = useContext(MyContext)
  const [labels, setLabels] = useState<string[]>([]);
  const [chartData, setChartData] = useState<number[]>([]);
  const [chartStats, setChartStats] = useState({
    minValue: 0,
    maxValue: 0,
    mean: 0,
    stdDev: 0,
    coefficientOfVariation: 0,
  });

  useEffect(() => {
    if (!selectedMetric && metrics.length > 0) {
      const firstValidMetric = metrics.find((metric) =>
        metric.data.some((dataItem) =>
          dataItem.work_item_types?.some((type: { datasets: string | any[]; }) => type.datasets?.length > 0)
        )
      );
      if (firstValidMetric) {
        setSelectedMetric(firstValidMetric.name);
      }
    }
  }, [metrics, selectedMetric]);


  useEffect(() => {
    if (metrics.length > 0) {
      setLoading(true);  
      const selected = metrics.find((metric) => metric.name === selectedMetric);

      if (selected) {
        const { units, data, current_release } = selected;
        let labelsMapset: Record<string, number> = {};
        let weightedValues: { weight: number }[] = [];
        let releaseMeanValues: number[] = [];

        
        const selectedBreakdownData =
          selectedBreakdownType === BreakdownType.CURRENT_RELEASE
            ? current_release || [] 
            : data;

          selectedBreakdownData.forEach((dataItem: { work_item_types: { count: any; datasets?: any[];type:string;value:number; }[]; }) => {
          if (Array.isArray(dataItem.work_item_types)) {
            let releaseWeightedSum = 0;
            let releaseTotalCount = 0;
            dataItem.work_item_types.forEach(({ count, datasets, type, value }) => {
  
              if (Array.isArray(datasets) && datasets.length > 0) {
                datasets.forEach(({ set, value }) => {
                  const scaledValue = selected.units === "work items" ? value : value * count;
                  labelsMapset[set] = (labelsMapset[set] || 0) + scaledValue;
                  releaseWeightedSum += scaledValue;
                });
              }else if (typeof value === "number") {
                const scaledValue = selected.units === "work items" ? value : value * count;
                labelsMapset[type] = (labelsMapset[type] || 0) + scaledValue;
                releaseWeightedSum += scaledValue;
              }
  
              releaseTotalCount += count;
            });
            weightedValues.push({ weight: releaseTotalCount });
            if (releaseTotalCount > 0 && units !=="work items") {
              releaseMeanValues.push(releaseWeightedSum / releaseTotalCount);
            }else{
              releaseMeanValues.push(releaseWeightedSum);
            }
          }
        });

        let totalSetSum = 0;
        const totalWeight = weightedValues.reduce((sum, { weight }) => sum + weight, 0);

        if (units !== "work items") {
          Object.keys(labelsMapset).forEach((set) => {
            labelsMapset[set] = (labelsMapset[set] / totalWeight);
            totalSetSum += labelsMapset[set];
          });
        } else {
          Object.keys(labelsMapset).forEach((set) => {
            labelsMapset[set] = (labelsMapset[set] / selectedBreakdownData.length);
            totalSetSum += labelsMapset[set];
          });
        }

        const meanValue = totalWeight > 0 ? totalSetSum : 0;
        const valueWithUnits = Number.isInteger(meanValue)
          ? `${meanValue} ${units || ""}`
          : `${meanValue.toFixed(1)} ${units || ""}`;


        const variance =
          totalWeight > 0
            ? releaseMeanValues.reduce(
                (sum, value, index) =>
                  sum + weightedValues[index].weight * Math.pow(value - meanValue, 2),
                0
              ) / totalWeight
            : 0;

        const stdDev = Math.sqrt(variance);
        const coefficientOfVariation = meanValue !== 0 ? stdDev / meanValue : 0;

        const minValue = releaseMeanValues.length > 0 ? Math.min(...releaseMeanValues) : 0;
        const maxValue = releaseMeanValues.length > 0 ? Math.max(...releaseMeanValues) : 0;



        setChartStats({
          minValue: Number(minValue.toFixed(1)),
          maxValue: Number(maxValue.toFixed(1)),
          mean: Number(meanValue.toFixed(1)),
          stdDev: Number(stdDev.toFixed(1)),
          coefficientOfVariation: Number(coefficientOfVariation.toFixed(1)),
        });

        const labels = Object.keys(labelsMapset);
        const chartData = Object.values(labelsMapset);
        setAverageValue(valueWithUnits);
        setLabels(labels);
        setChartData(chartData);
      }
      setLoading(false);
    }
  }, [metrics, selectedMetric,selectedBreakdownType]);


  const filteredMetric = metrics?.find((metric) => metric.name === selectedMetric);

  return (
    <>
      <div className="flex items-center gap-2 mb-3">
      <h3 className="card-title" style={{ margin: 0 }}>Breakdown of</h3>
        <Select
          value={selectedBreakdownType}
          className="w-[160px]"
          onChange={(value) => {
            setSelectedBreakdownType(value);
            setLoading(true);
            updateFilter("breakDownType", value);
          }}
          dropdownStyle={{ minWidth: "120px" }}
        >
          {Object.entries(BreakdownType).map(([key, value]) => {
              let label =
                value === BreakdownType.CURRENT_RELEASE
                  ? filteredMetric?.current_release?.[0]?.release || BreakdownType.CURRENT_RELEASE 
                  : key.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

              return (
                <Select.Option key={value} value={value} className="font-bold">
                  {label}
                </Select.Option>
              ); 
            })}
        </Select>

        <Select
          value={selectedMetric}
          className="w-[140px]"
          onChange={(value) => {
            setSelectedMetric(value);
            setLoading(true);
            updateFilter("releaseBreakdown", value);
          }}
        >
          {metrics
            ?.filter((metric) =>
              metric.show_breakdown &&
              metric.data.some((dataItem) =>
                dataItem.work_item_types?.some(
                  (type: { datasets: string | any[] }) => type.datasets?.length > 0
                )
              )
            )
            ?.map((metric) => (
              <Select.Option key={metric.name} value={metric.name}>
                {metric.name}
              </Select.Option>
            ))}
        </Select>

      </div>

      {filteredMetric && filteredMetric.data.length > 0 ? (
        <DoughnutChart metric={filteredMetric} averageValue={averageValue} loading={loading} labels={labels} chartData={chartData} chartStats={chartStats} selectedBreakdownType={selectedBreakdownType}/>
      ) : (
        <div className="flex items-center justify-center h-64 text-gray-500 font-medium text-lg">
          No data available.
        </div>
      )}

    </>
  );
};

export default MetricDropdown;
